import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PREVIEW_BREAKPOINT } from "../../../../components/AppShell/styles";
import { Button, LinkButton } from "../../../../components/Button";
import ConfirmModal from "../../../../components/ConfirmModal";
import * as Fonts from "../../../../components/Fonts";
import { Box, Container as BarContainer, StickySpacer } from "../../../../components/SaveBar/styles";
import { ScreenHeader } from "../../../../components/SubHeader";
import { AppState, useAppState } from "../../../../contexts/App";
import { useDraftSitePreview, usePreviewSite } from "../../../../contexts/PreviewSite";
import { useSite } from "../../../../contexts/Site";
import CheckIcon from "../../../../icons/Check";
import { OTHER_SEGMENT, parseCategory } from "../../../../model/categories";
import { siteHasContent } from "../../../../model/pages";
import { Section as SiteSection, Site, WelcomeSection } from "../../../../model/site";
import { api, KeepoTemplate, KeepoTemplateSummary } from "../../../../services/api";
import { templatesService } from "../../../../services/templates";
import { isPlusRequiredCreation } from "../../../../utils/plusRequiredCreation";
import { uuidv4 } from "../../../../utils/uuid";
import CategoriesInputSelect from "../../components/CategoriesInputSelect";
import CreationProgress from "../../components/CreationProgress";
import { Container, Section } from "../Title/styles";
import { MobileTemplatePreview } from "./MobileTemplatePreview";
import { ColorFilter, Empty, ErrorNote, FilterCard, Grid, Thumb, ThumbSkeleton } from "./styles";

/** Base colors of the templates (as the API classifies them) and the swatch shown for each. */
const TEMPLATE_COLORS: Record<string, string> = {
  red: "#EF4444",
  orange: "#F97316",
  yellow: "#FACC15",
  green: "#22C55E",
  blue: "#3B82F6",
  purple: "#8B5CF6",
  pink: "#EC4899",
  brown: "#92400E",
  black: "#000000",
  white: "#FFFFFF",
  gray: "#9CA3AF",
};

/** How long the preview shows the loading when a template is picked (ms). */
const PICK_DELAY = 500;

const prefix = "pages.editor.templates";

/**
 * The Keepo built from a template: its theme, layout and sections (each with a new id, as if added
 * in the editor), keeping what the user already set (title, field of work). The welcome section shows
 * the Keepo's own name instead of the template's sample one.
 */
const fromTemplate = (site: Site, template: KeepoTemplate): Site => ({
  ...site,
  theme: template.content.theme || site.theme,
  layout: template.content.layout || site.layout,
  sections: (template.content.sections || []).map(section =>
    section.type === "welcome"
      ? ({ ...section, id: uuidv4(), exibitionName: site.title } as WelcomeSection)
      : ({ ...section, id: uuidv4() } as SiteSection)
  ),
});

/**
 * "Usar um modelo": the field of work in the center, the templates for it as thumbnails below and
 * the picked one in the side preview. Continuing fills the new Keepo's draft with it.
 */
const TemplatesPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { appState, setAppState } = useAppState();
  const { site, setSite } = useSite();
  const { setPreviewLoading, setPreviewHint } = usePreviewSite();

  const [category, setCategory] = useState(site?.category || "");
  const [categoryOther, setCategoryOther] = useState(site?.categoryOther || "");
  const [templates, setTemplates] = useState<KeepoTemplateSummary[]>();
  const [listError, setListError] = useState(false);
  const [selected, setSelected] = useState<KeepoTemplate>();
  // Back from the editor: the template already applied shows picked, with the Keepo as it is now
  // (edits included), until another one is picked
  const siteId = appState.editingSiteId as string;
  const [appliedId] = useState(() => templatesService.getApplied(siteId));
  const [keepCurrent, setKeepCurrent] = useState(!!appliedId);
  const selectedId = keepCurrent ? appliedId : selected?.id;
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [loadingId, setLoadingId] = useState<string>();
  const [failedImages, setFailedImages] = useState<string[]>([]);
  const [loadedImages, setLoadedImages] = useState<string[]>([]);
  const [isSaving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [isMobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [color, setColor] = useState<string>();

  const parsed = parseCategory(category);
  const segment = parsed.kind === "segment" ? parsed.segment : "";
  const specialty = parsed.kind === "segment" ? parsed.specialty : "";

  // The list follows the field of work; the template already picked stays in the preview (and can still
  // be used) while browsing other fields
  useEffect(() => {
    setColor(undefined);
    setListError(false);
    if (!segment) {
      setTemplates([]);
      return;
    }
    let cancelled = false;
    setTemplates(undefined);
    templatesService
      .list(segment, specialty, i18n.language)
      .then(list => {
        if (cancelled) {
          return;
        }
        setTemplates(list);
      })
      .catch(() => {
        if (!cancelled) {
          setTemplates([]);
          setListError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [segment, specialty, i18n.language]);

  // Contents are loaded ahead only for the thumbnails on screen (and the one under the pointer), so
  // picking one switches right away without fetching a whole long list
  const preload = useCallback(
    (id: string) => templatesService.get(id, i18n.language).catch(() => undefined),
    [i18n.language]
  );
  const [visibility, setVisibility] = useState<IntersectionObserver>();
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries =>
        entries.forEach(entry => {
          const id = (entry.target as HTMLElement).dataset.templateId;
          if (entry.isIntersecting && id) {
            preload(id);
            observer.unobserve(entry.target);
          }
        }),
      { rootMargin: "200px 0px" }
    );
    setVisibility(observer);
    return () => observer.disconnect();
  }, [preload]);
  // A new observer (language changed) gets every thumbnail again: the ref callback changes with it
  const observeThumb = useCallback(
    (element: HTMLButtonElement | null) => element && visibility?.observe(element),
    [visibility]
  );

  // Until a template is picked, the preview asks for one instead of showing the empty Keepo
  useEffect(() => {
    setPreviewHint(
      selectedId ? undefined : { title: t(`${prefix}.previewHint.title`), text: t(`${prefix}.previewHint.text`) }
    );
  }, [selectedId, setPreviewHint, t]);
  useEffect(() => () => setPreviewHint(undefined), [setPreviewHint]);

  // The preview's loading state belongs to this screen: never leave it on
  useEffect(() => () => setPreviewLoading(false), [setPreviewLoading]);

  // Only the colors some template of the list has, so a filter never leads to an empty list
  const colors = useMemo(
    () => Object.keys(TEMPLATE_COLORS).filter(key => templates?.some(template => template.colors?.includes(key))),
    [templates]
  );
  const shownTemplates = color ? templates?.filter(template => template.colors?.includes(color)) : templates;

  const draft = useMemo(
    () =>
      site
        ? ({
            ...(!keepCurrent && selected ? fromTemplate(site, selected) : site),
            category,
            categoryOther: categoryOther.trim() || undefined,
          } as Site)
        : undefined,
    [site, selected, keepCurrent, category, categoryOther]
  );
  useDraftSitePreview(draft);

  // Each pick shows the preview loading for at least PICK_DELAY, so the switch reads as the template
  // being applied; only the last click counts
  const lastPick = useRef<string>();
  const pick = async (template: KeepoTemplateSummary) => {
    // Without the side preview, the template opens full screen
    if (!window.matchMedia(`(min-width: ${PREVIEW_BREAKPOINT})`).matches) {
      setMobilePreviewOpen(true);
    }
    // The one already shown (or on its way) stays as is
    if (template.id === (loadingId || selectedId)) {
      return;
    }
    lastPick.current = template.id;
    setLoadingId(template.id);
    setPreviewLoading(true);
    try {
      // The one already applied: back to the Keepo as it is, nothing to load
      const isApplied = template.id === appliedId;
      const [content] = await Promise.all([
        isApplied ? undefined : templatesService.get(template.id, i18n.language),
        new Promise(resolve => setTimeout(resolve, PICK_DELAY)),
      ]);
      if (lastPick.current === template.id) {
        setKeepCurrent(isApplied);
        if (content) {
          setSelected(content);
        }
      }
    } catch {
      if (lastPick.current === template.id) {
        setListError(true);
      }
    } finally {
      if (lastPick.current === template.id) {
        setLoadingId(undefined);
        setPreviewLoading(false);
      }
    }
  };

  const canContinue = (keepCurrent || !!selected) && !!draft && !isSaving && !loadingId;

  const continueWithTemplate = async (confirmed = false) => {
    if (!canContinue || !draft) {
      return;
    }
    // A template over one already applied, or over a Keepo already built (by the AI, or by hand), replaces what the
    // Keepo has (edits included)
    if ((appliedId || siteHasContent(site)) && !keepCurrent && !confirmed) {
      setShowReplaceConfirm(true);
      return;
    }
    setShowReplaceConfirm(false);
    setSaving(true);
    setSaveError(false);
    const toSave: Site = isPlusRequiredCreation(appState.editingSiteId as string)
      ? { ...draft, intendedSubscription: "PLUS" }
      : draft;
    try {
      await api.saveDraft(appState.editingSiteId as string, toSave);
      setSite(draft);
      setAppState({ ...appState, editingSiteModified: true } as AppState);
      if (selectedId) {
        templatesService.setApplied(siteId, selectedId);
      }
      // A template just applied (not the one kept): the Keepo remembers it, for the template reports. Not
      // waited for, nor in the way: the Keepo is created either way
      if (!keepCurrent && selected) {
        api.saveTemplate(siteId, selected.id).catch(() => undefined);
      }
      // its pages first: the home (and any the template has), to edit or add to
      navigate("/editor/pages");
    } catch {
      setSaveError(true);
      setSaving(false);
    }
  };

  return (
    <Container>
      {/* where the person is in the flow, on top; the screen's own header under it */}
      <CreationProgress step="template" />
      <ScreenHeader title={t(`${prefix}.title`)} back={{ label: t(`${prefix}.back`), to: "/editor/start" }} />

      <Section>
        <FilterCard>
          <CategoriesInputSelect
            id="templateCategory"
            required
            value={category}
            other={categoryOther}
            disableOtherText
            onChange={value => {
              setCategory(value);
              // No text asked here: "other" keeps the words the Keepo already had
              setCategoryOther(value === OTHER_SEGMENT ? site?.categoryOther || "" : "");
            }}
          />
          {colors.length > 1 && (
            <ColorFilter role="group" aria-labelledby="templateColor-label">
              <Fonts.Label id="templateColor-label">{t(`${prefix}.color.label`)}</Fonts.Label>
              <div className="swatches">
                <button type="button" className="all" aria-pressed={!color} onClick={() => setColor(undefined)}>
                  {t(`${prefix}.color.all`)}
                </button>
                {colors.map(key => (
                  <button
                    key={key}
                    type="button"
                    className="swatch"
                    style={{ background: TEMPLATE_COLORS[key] }}
                    aria-pressed={color === key}
                    aria-label={t(`${prefix}.color.options.${key}`)}
                    title={t(`${prefix}.color.options.${key}`)}
                    onClick={() => setColor(key)}
                  />
                ))}
              </div>
            </ColorFilter>
          )}
        </FilterCard>
      </Section>

      <Section>
        <Fonts.Label>{t(`${prefix}.list`)}</Fonts.Label>
        {templates === undefined ? (
          <Grid aria-busy="true">
            {[0, 1, 2, 3].map(key => (
              <ThumbSkeleton key={key} />
            ))}
          </Grid>
        ) : shownTemplates?.length ? (
          <Grid role="radiogroup">
            {shownTemplates.map(template => {
              const isSelected = selectedId === template.id;
              const showImage = !!template.thumbnail && !failedImages.includes(template.id);
              return (
                <Thumb
                  key={template.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={template.name}
                  title={template.name}
                  aria-busy={loadingId === template.id}
                  $selected={isSelected}
                  ref={observeThumb}
                  data-template-id={template.id}
                  onMouseEnter={() => preload(template.id)}
                  onFocus={() => preload(template.id)}
                  onClick={() => pick(template)}>
                  {/* Shimmers until the image is in, which then fades in whole (never drawn bit by bit) */}
                  <span className={showImage && !loadedImages.includes(template.id) ? "frame loading" : "frame"}>
                    {showImage ? (
                      <img
                        src={template.thumbnail as string}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        onLoad={() => setLoadedImages(current => [...current, template.id])}
                        onError={() => setFailedImages(current => [...current, template.id])}
                      />
                    ) : (
                      <span className="fallback">{template.name}</span>
                    )}
                    {isSelected && (
                      <span className="check">
                        <CheckIcon width={12} height={12} color="currentColor" />
                      </span>
                    )}
                  </span>
                </Thumb>
              );
            })}
          </Grid>
        ) : (
          <Empty>
            <p>{listError ? t(`${prefix}.error`) : t(`${prefix}.empty`)}</p>
            <button type="button" onClick={() => navigate("/editor/pages")}>
              {t(`${prefix}.fromScratch`)}
            </button>
          </Empty>
        )}
        {listError && !!templates?.length && <ErrorNote>{t(`${prefix}.error`)}</ErrorNote>}
        {saveError && <ErrorNote>{t(`${prefix}.saveError`)}</ErrorNote>}
      </Section>

      <MobileTemplatePreview
        open={isMobilePreviewOpen}
        name={templates?.find(({ id }) => id === (loadingId || selectedId))?.name || selected?.name}
        site={draft || (site as Site)}
        loading={!!loadingId}
        canUse={canContinue}
        saving={isSaving}
        error={saveError ? t(`${prefix}.saveError`) : undefined}
        onClose={() => setMobilePreviewOpen(false)}
        onUse={() => continueWithTemplate()}
      />

      {showReplaceConfirm && (
        <ConfirmModal
          type="INFO"
          title={t(appliedId ? `${prefix}.replaceConfirm.title` : `${prefix}.replaceConfirm.titleBuilt`)}
          descriptions={[
            t(appliedId ? `${prefix}.replaceConfirm.description` : `${prefix}.replaceConfirm.descriptionBuilt`),
          ]}
          confirmButtonTitle={t(`${prefix}.replaceConfirm.confirm`)}
          cancelTitle={t(`${prefix}.replaceConfirm.cancel`)}
          onConfirm={() => continueWithTemplate(true)}
          onCancel={() => setShowReplaceConfirm(false)}
        />
      )}

      {/* Same fixed bar as the editor's save bar */}
      <StickySpacer />
      <Box $sticky>
        <BarContainer>
          <LinkButton onClick={() => navigate("/editor/start")}>{t(`${prefix}.back`)}</LinkButton>
          <Button
            type="INFO"
            size="LG"
            disabled={!canContinue}
            $loading={isSaving}
            onClick={() => canContinue && continueWithTemplate()}>
            {t(`${prefix}.continue`)}
          </Button>
        </BarContainer>
      </Box>
    </Container>
  );
};

export default TemplatesPage;
